import React, { useMemo, useState } from 'react';
import Pagination from '@mui/material/Pagination';
import MenuListComposition from './tempComps/MUIFIlter';
import ClaimStatusCard from './InfoCard';
import { DuplicateClaimIcon, InboundClaimIcon, OutboundClaimIcon, RejectedClaimIcon } from '../helpers/Icons';
import { useLocation } from 'react-router-dom';

const SearchResults = ({ data, onViewItem, cardInfo }) => {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ date: null, status: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  const location = useLocation();

  const showClaimStatusCards = location.pathname === '/dashboard/files' || location.pathname === '/dashboard/physician';
  const handleFilterChange = (filterKey, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [filterKey]: value }));
  };
  console.log(cardInfo);
  const applyFilters = () => {
    setShowFilter(false);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    const newDirection = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction: newDirection });
  };
  const sortedAndFilteredData = useMemo(() => {
    let filteredData = Object.keys(data).filter((key) => {
      const item = data[key];

      const itemDate = new Date(item.dos);
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      const isWithinRange = (!startDate || itemDate >= startDate) && (!endDate || itemDate <= endDate);

      if (!isWithinRange) return false;
      if (filters.status && item.status !== filters.status) return false;
      if (filters.direction && item.direction !== filters.direction) return false;

      return true;
    });

    if (sortConfig.key) {
      filteredData.sort((aKey, bKey) => {
        const a = data[aKey][sortConfig.key];
        const b = data[bKey][sortConfig.key];

        if (a < b) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a > b) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filteredData;
  }, [data, filters, sortConfig]);

  const claimCards = [
    {
      status: 'Inbound Claims',
      amount: cardInfo?.total_inbound,
      icon: <InboundClaimIcon />,
      classNames: 'border border-[#BFACDE] bg-[#F1EAFC]',
    },
    {
      status: 'Outbound Claims',
      amount: cardInfo?.total_outbound,
      icon: <OutboundClaimIcon />,
      classNames: 'border border-[#ADC0D5] bg-[#E6F1FD]',
    },
    {
      status: 'Duplicate Claims',
      amount: cardInfo?.total_duplicate,
      icon: <DuplicateClaimIcon />,
      classNames: 'border border-[#BFACDE] bg-[#F1EAFC]',
    },
    {
      status: 'Rejected Claims',
      amount: cardInfo?.total_rejected,
      icon: <RejectedClaimIcon />,
      classNames: 'border border-[#ADC0D5] bg-[#E6F1FD]',
    },
  ];

  const totalPages = Math.ceil(sortedAndFilteredData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedAndFilteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedAndFilteredData, currentPage, itemsPerPage]);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="relative">
      <div className="flex justify-between items-center py-4 relative">
        <h1 className="text-2xl font-normal text-[#3A3541]">Search Results</h1>
        <div className="z-10">
          <MenuListComposition filters={filters} onChange={handleFilterChange}
                               onApply={applyFilters} />
        </div>
      </div>
      {showClaimStatusCards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {claimCards.map((card, index) => (
            <ClaimStatusCard
              key={index}
              status={card.status}
              amount={card.amount}
              icon={card.icon}
              classNames={card.classNames}
            />
          ))}
        </div>

      )}
      <div className="flex justify-between items-center mb-6 mt-6">
        <div className="flex justify-center items-center mt-4">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="secondary"
            sx={{
              '& .MuiPaginationItem-root': {
                '&.Mui-selected': {
                  backgroundColor: '#6E39CB',
                },
              },
            }}
          />
        </div>
        <div>
          <label htmlFor="rowsPerPage" className="mr-2 text-gray-700">
            Show
          </label>
          <select
            id="rowsPerPage"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="border-none bg-white text-gray-700 z-10 focus:outline-0 focus:border-none">
            {[5, 10, 15, 20, 30, 50, 100, 200, 300, 500].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="relative overflow-auto">
        <table className="table-auto w-full bg-white border-collapse border border-gray-300 rounded-xl">
          <thead>
          <tr className="bg-gray-100">
            {[
              { label: 'Member ID', key: 'pat_member_id' },
              { label: 'Date Received', key: 'pat_dob' },
              { label: 'Date of Service', key: 'dos' },
              { label: 'Physician NPI', key: 'physicians_npi' },
              { label: 'Patient Last Name', key: 'pat_lname' },
              { label: 'Status', key: 'status' },
              { label: 'Actions', key: '' },
            ].map(({ label, key }) => (
              <th
                key={key || label}
                className="text-left px-4 py-2 text-gray-700 cursor-pointer"
                onClick={() => key && handleSort(key)}>
                {label}
                {key && (
                  <span className="ml-2 text-sm text-gray-500">
                      {sortConfig.key === key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
                    </span>
                )}
              </th>
            ))}
          </tr>
          </thead>
          <tbody>
          {paginatedData.map((key, index) => {
            const item = data[key];
            return (
              <tr key={index} className="border-t">
                <td className="px-4 py-2 text-gray-700">{item.pat_member_id}</td>
                <td className="px-4 py-2 text-gray-700">{item.pat_dob}</td>
                <td className="px-4 py-2 text-gray-700">{item.dos}</td>
                <td className="px-4 py-2 text-gray-700">{item.physicians_npi}</td>
                <td className="px-4 py-2 text-gray-700">{item.pat_lname}</td>
                <td className="px-4 py-2 text-gray-700">
                    <span className={`font-semibold ${item.status === 'Approved' ? 'text-green-600' : 'text-red-600'}`}>
                      {item.status}
                    </span>
                </td>
                <td className="px-4 py-2 text-center">
                  <button
                    onClick={() => onViewItem(item.id)}
                    className="px-4 py-2 border border-purple-500 text-purple-500 rounded-md hover:bg-purple-50">
                    View
                  </button>
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
        {paginatedData.length === 0 && <p className="text-gray-500 text-center mt-4">No results found.</p>}
      </div>
    </div>
  );
};

export default SearchResults;
